import { useEffect, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import io from "socket.io-client";
import { App as capacitorApp } from "@capacitor/app";
import localforage from "localforage";

export function useAppEffects(API_URL, state, externalMethods) {
  const {
    setIsMobile,
    setIsLoggedIn,
    setCurrentRadius,
    setCurrentLocation,
    isLoggedInRef,
    userIdRef,
    socketRef,
    timeoutRef,
    setQueueData,
    setCurrentNumber,
    setQueueStatus,
    setBusinessMessage,
    selectedBusinessTypeRef,
    setSelectedBusinessType
  } = state;

  const { fetchBusinessTypes, fetchQueueInfo, requestLocation } = externalMethods;

  const handleBackButton = useCallback(() => {
    if (selectedBusinessTypeRef.current) {
      setSelectedBusinessType(null);
    } else {
      capacitorApp.exitApp();
    }
  }, [selectedBusinessTypeRef, setSelectedBusinessType]);

  const initializeApp = useCallback(async () => {
    const token = await localforage.getItem("access_token");
    const radius = await localforage.getItem("radius");
    if (radius) setCurrentRadius(Number(radius));

    if (token) {
      setIsLoggedIn(true);
      await fetchQueueInfo(token);
    } else {
      const location = await localforage.getItem("LocationDetails");
      if (location) {
        setCurrentLocation(location);
      } else {
        requestLocation(true);
      }
      fetchBusinessTypes();
    }
  }, [fetchBusinessTypes, fetchQueueInfo, requestLocation, setCurrentRadius, setIsLoggedIn, setCurrentLocation]);

  useEffect(() => {
    const detectPlatform = async () => {
      const Platform = await Capacitor.getPlatform();
      setIsMobile(Platform === "ios" || Platform === "android");
    };

    detectPlatform();
    initializeApp();
    capacitorApp.addListener("backButton", handleBackButton);

    const socket = io(API_URL, {
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      secure: true,
    });
    socketRef.current = socket;

    const handleQueueUpdate = (data) => {
      if (isLoggedInRef.current && userIdRef.current === data.userId) {
        if (data.hasOwnProperty("queueNumber"))
          setCurrentNumber(data.queueNumber);
        if (data.hasOwnProperty("isActive")) setQueueStatus(data.isActive);
        if (data.hasOwnProperty("businessMessage"))
          setBusinessMessage(data.businessMessage);
      } else {
        setQueueData((prevQueueData) =>
          prevQueueData.map((queue) =>
            queue.userId === data.userId ? { ...queue, ...data } : queue
          )
        );
      }
    };

    socket.on("queueUpdate", handleQueueUpdate);

    return () => {
      socket.off("queueUpdate", handleQueueUpdate);
      socket.disconnect();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      capacitorApp.removeAllListeners();
    };
  }, [
    API_URL,
    initializeApp,
    handleBackButton,
    setIsMobile,
    isLoggedInRef,
    userIdRef,
    socketRef,
    timeoutRef,
    setCurrentNumber,
    setQueueStatus,
    setBusinessMessage,
    setQueueData
  ]);
}
